// MARK://dhk8/pos/pages/OrderPage
// POS comanda surface, redesigned for the 534×738 tablet-portrait target (≈462px
// of content width after the nav rail). Square-on-iPad-portrait pattern:
//   • compact one-line order-context header (mesa · comensales · folio/mesero),
//   • full-width 2-column menu grid (search + horizontally-scrolling category pills),
//   • cart as a bottom sheet: a sticky bottom bar opens a sheet over ~78% of the
//     screen with the order items (grouped under sub-account tabs when present),
//     totals pinned at the bottom, and Enviar a cocina / Dividir / Cobrar actions.
// SplitDialog / DiscountDialog / PaymentDialog open as full-screen modals on top.
// All order mutations still go through order-store — only the layout changed.
// Port of sc-restaurant PosPage.tsx onto the new stack.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Search as SearchIcon, X as XIcon, ShoppingCart, ChevronRight, CloudOff,
  SplitSquareHorizontal, Percent, Flame, DollarSign, Plus, Minus, Trash2, FileText,
  Ban, Gift,
} from 'lucide-react'
import type { ProductDto } from '@dockethub/ui-restaurant'
import { apiOrigin } from '../lib/api-origin.js'
import { apiFetch } from '../lib/api.js'
import { useOrderStore, getPersistedOrderId, PRINCIPAL_SUB_ID, type DraftItem, type OrderItem } from '../stores/order-store.js'
import { useAuthStore } from '../stores/auth-store.js'
import { useActiveMenu } from '../hooks/useCatalog.js'
import { useTenantId } from '../hooks/useTenantId.js'
import { resolveUnitPrice } from '../lib/clients/pricing.js'
import { formatCents } from '../lib/money.js'
import TableSelectorBar from '../features/TableSelectorBar.js'
import PaymentDialog from '../features/PaymentDialog.js'
import DiscountDialog from '../features/DiscountDialog.js'
import { useFireKitchenPrint, useEnqueuePrecuenta } from '../hooks/usePrinting.js'
import AddItemDialog from '../features/AddItemDialog.js'
import BottomSheet from '../features/pos/BottomSheet.js'
import SplitDialog, { type SplitResult } from '../features/SplitDialog.js'
import { PREVIEW_STUB, SEED_ORDER, previewProductExtras } from '../lib/seed-preview.js'
import { requireManagerAuth } from '../features/security/pin-gate-store.js'
import { updateOrderItem, cancelOrder } from '../lib/clients/orders.js'

export default function OrderPage() {
  const { user } = useAuthStore()
  const order = useOrderStore((s) => s.order)
  const addItem = useOrderStore((s) => s.addItem)
  const updateItemQty = useOrderStore((s) => s.updateItemQty)
  const hydrateFromBackend = useOrderStore((s) => s.hydrateFromBackend)
  const syncing = useOrderStore((s) => s.syncing)
  const setTable = useOrderStore((s) => s.setTable)
  const removeItem = useOrderStore((s) => s.removeItem)
  const sendToKitchen = useOrderStore((s) => s.sendToKitchen)
  const fireKitchenPrint = useFireKitchenPrint()
  const enqueuePrecuenta = useEnqueuePrecuenta()
  const pendingItemIds = useOrderStore((s) => s.pendingItemIds)
  const resetOrder = useOrderStore((s) => s.resetOrder)
  const outboxDepth = useOrderStore((s) => s.outboxDepth)
  const flushOutbox = useOrderStore((s) => s.flushOutbox)
  const splitOrder = useOrderStore((s) => s.splitOrder)
  const assignItemToSub = useOrderStore((s) => s.assignItemToSub)
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = useTenantId()

  // On tenant change (env switch Test ↔ Live), the in-memory order + the
  // persisted order id belong to the previous tenant — drop them so we
  // don't try to hydrate a foreign order on the new tenant. Bug #81.
  const firstTenantRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (firstTenantRef.current === undefined) {
      firstTenantRef.current = tenantId
      return
    }
    if (firstTenantRef.current !== tenantId) {
      firstTenantRef.current = tenantId
      resetOrder()
    }
  }, [tenantId, resetOrder])

  const menu = useActiveMenu()

  const [pickedProduct, setPickedProduct] = useState<ProductDto | null>(null)
  const [topError, setTopError] = useState<string | null>(null)
  const [paymentOpen, setPaymentOpen] = useState(false)
  const [splitOpen, setSplitOpen] = useState(false)
  const [discountOpen, setDiscountOpen] = useState(false)
  const [cartOpen, setCartOpen] = useState(false)
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [busyItemId, setBusyItemId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [activeCat, setActiveCat] = useState<string | 'ALL'>('ALL')
  const [activeSub, setActiveSub] = useState<string | null>(null)

  const showToast = (msg: string, kind: 'success' | 'error' = 'success') => {
    setToast({ msg, kind })
    setTimeout(() => setToast(null), 2500)
  }

  // ── Mount rehydrate ───────────────────────────────────────────────────
  const orderIdParam = searchParams.get('orderId')
  const payParam = searchParams.get('pay')
  useEffect(() => {
    let cancelled = false
    const targetId = orderIdParam ?? getPersistedOrderId() ?? (PREVIEW_STUB ? SEED_ORDER.id : null)
    if (!targetId) return
    void (async () => {
      try {
        const fresh = await hydrateFromBackend(targetId)
        if (cancelled || !fresh) return
        setTable({ tableSessionId: fresh.tableSessionId ?? null, covers: fresh.covers ?? 1, waiterName: user?.fullName ?? null })
        // "Cobrar" directo desde Tickets: /order?orderId=X&pay=1 abre el cobro de una
        // vez si la orden es pagable (abierta y con ítems vivos). "Reanudar" omite pay.
        if (payParam === '1') {
          const o = useOrderStore.getState().order
          if (o && o.status !== 'CLOSED' && o.status !== 'CANCELLED' && o.items.some((i) => i.status !== 'CANCELLED')) {
            setPaymentOpen(true)
          }
        }
        if (orderIdParam) setSearchParams({}, { replace: true })
      } catch (e) {
        showToast(e instanceof Error ? e.message : 'No se pudo cargar la orden', 'error')
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderIdParam])

  // ── Barcode scanner ───────────────────────────────────────────────────
  const scanBufferRef = useRef<{ chars: string[]; last: number }>({ chars: [], last: 0 })
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      const now = performance.now()
      const buf = scanBufferRef.current
      if (now - buf.last > 50 && buf.chars.length > 0) buf.chars = []
      buf.last = now
      if (e.key === 'Enter') {
        const code = buf.chars.join('')
        buf.chars = []
        if (code.length >= 4) { e.preventDefault(); void handleBarcode(code) }
        return
      }
      if (e.key.length === 1) buf.chars.push(e.key)
    }
    async function handleBarcode(code: string) {
      try {
        const found = await apiFetch<{ id: string; name: string; unitPriceCents: number; taxRate: string } | null>(
          `${apiOrigin()}/v1/catalog-products/lookup?barcode=${encodeURIComponent(code)}`,
        )
        if (!found) { showToast(`Producto no encontrado: ${code}`, 'error'); return }
        if (!useOrderStore.getState().order) { showToast('Abre una mesa antes de escanear', 'error'); return }
        await addItem({ productId: found.id, name: found.name, quantity: 1, unitPriceCents: found.unitPriceCents, taxRate: found.taxRate, modifiers: [] })
        showToast(`Escaneado: ${found.name}`)
      } catch (e) { showToast(e instanceof Error ? e.message : 'Error en lookup', 'error') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Menu filtering ────────────────────────────────────────────────────
  const products = menu.data?.products ?? []
  const categories = useMemo(() => {
    const inUse = new Set(products.map((p) => p.categoryId).filter(Boolean) as string[])
    return (menu.data?.categories ?? []).filter((c) => inUse.has(c.id)).sort((a, b) => a.sortOrder - b.sortOrder)
  }, [menu.data?.categories, products])
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return products.filter((p) => {
      if (activeCat !== 'ALL' && p.categoryId !== activeCat) return false
      if (q.length === 0) return true
      return p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q)
    })
  }, [products, activeCat, search])

  function handleSelectProduct(product: ProductDto) {
    if (!order) { setTopError('Primero abre una mesa antes de agregar productos'); return }
    setTopError(null)
    setPickedProduct(product)
  }

  async function withResolvedPrice(draft: DraftItem): Promise<DraftItem> {
    const branchId = useAuthStore.getState().user?.branchId
    if (!branchId) return draft
    const unitPriceCents = await resolveUnitPrice({ productId: draft.productId, basePriceCents: draft.unitPriceCents, quantity: draft.quantity, branchId, channel: 'DINE_IN' })
    return unitPriceCents === draft.unitPriceCents ? draft : { ...draft, unitPriceCents }
  }

  async function handleAddItem(draft: DraftItem) {
    try {
      await addItem(await withResolvedPrice(draft))
      showToast(`Agregado: ${draft.quantity}× ${draft.name}`)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo agregar', 'error')
      throw e
    }
  }

  async function handleAddItemQty(productId: string) {
    const p = menu.data?.products.find((x) => x.id === productId)
    if (!p) { showToast('Producto no disponible', 'error'); return }
    // Si ya hay una línea PENDING (no enviada a cocina) del mismo producto SIN
    // modificadores ni notas, subimos su cantidad en lugar de duplicar la línea.
    // Si ya fue enviada (status !== PENDING) o tiene mods/notas, mantenemos
    // línea nueva para no contaminar lo que cocina ya está preparando.
    const existing = order?.items.find((i) =>
      i.productId === productId &&
      i.status === 'PENDING' &&
      (i.modifiers?.length ?? 0) === 0 &&
      !i.notes,
    )
    if (existing) {
      setBusyItemId(existing.id)
      try {
        await updateItemQty(existing.id, existing.quantity + 1)
      } catch (e) {
        showToast(e instanceof Error ? e.message : 'No se pudo incrementar', 'error')
      } finally { setBusyItemId(null) }
      return
    }
    setBusyItemId(productId)
    try {
      await addItem(await withResolvedPrice({ productId: p.id, name: p.name, unitPriceCents: p.unitPriceCents, taxRate: String(p.taxRate), quantity: 1, modifiers: [] }))
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo incrementar', 'error')
    } finally { setBusyItemId(null) }
  }

  async function handleRemoveItem(itemId: string) {
    // TCK-0016 (anti robo-hormiga): quitar un ítem SIEMPRE requiere autorización
    // de gerente desde el momento en que se agrega (antes solo si ya estaba
    // enviado a cocina). requireManagerAuth no le pide PIN al gerente/owner
    // (procede directo); a barra/cajero le pide el PIN y trae el token.
    const auth = await requireManagerAuth('order.voidItem')
    if (!auth.proceed) return
    setBusyItemId(itemId)
    try { await removeItem(itemId, auth.managerAuthToken) }
    catch (e) { showToast(e instanceof Error ? e.message : 'No se pudo quitar', 'error') }
    finally { setBusyItemId(null) }
  }

  // Lane #172 — Cortesía (regalo 100%) per-item. Pide manager-PIN + razón
  // obligatoria; setea `discountCents = lineNetCents` para que el cobrable
  // quede en $0 y anota la razón en `notes` para que aparezca en el ticket.
  // El backend recalcula taxCents/totalCents al recibir el PATCH.
  // NOTA: `OrderItem` del store no expone `discountCents`, sólo `totalCents`
  // (neto cobrable después de descuentos previos). Usar totalCents implica
  // que la cortesía cubre el faltante por pagar, no el bruto bruto; en el
  // caso normal (sin descuento previo) ambos coinciden.
  async function handleCompItem(itemId: string) {
    if (!order) return
    const item = order.items.find((it) => it.id === itemId)
    if (!item) return
    const fullDiscount = item.totalCents
    if (fullDiscount <= 0) return
    const auth = await requireManagerAuth('order.compItem', {
      requireReason: true,
      contextLabel: `${item.name} — ${formatCents(fullDiscount)}`,
    })
    if (!auth.proceed) return
    const reason = auth.reason
    setBusyItemId(itemId)
    try {
      const note = `Cortesía: ${(reason ?? '').trim()}`.slice(0, 240)
      await updateOrderItem(order.id, itemId, {
        discountCents: fullDiscount,
        notes: note,
      }, auth.managerAuthToken)
      // Re-hidrata el carrito desde el backend para que totales/línea queden
      // alineados con el cálculo del server.
      await hydrateFromBackend(order.id)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo aplicar la cortesía', 'error')
    } finally {
      setBusyItemId(null)
    }
  }

  // Lane #172 — Anular comanda completa. Pide manager-PIN + razón obligatoria;
  // marca la orden como CANCELLED (sin cobrar). Útil cuando la mesa entera se
  // levanta y deja una orden lanzada.
  async function handleVoidOrder() {
    if (!order) return
    if (order.status === 'CLOSED' || order.status === 'CANCELLED') return
    const auth = await requireManagerAuth('order.voidOrder', {
      requireReason: true,
      contextLabel: `Orden ${order.folio} — ${formatCents(order.totalCents)}`,
    })
    if (!auth.proceed) return
    const reason = auth.reason
    try {
      await cancelOrder(order.id, (reason ?? '').trim() || 'Anulada por gerente', auth.managerAuthToken)
      await hydrateFromBackend(order.id)
      setCartOpen(false)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo anular la comanda', 'error')
    }
  }

  async function handleSendToKitchen() {
    if (!order || pendingItemIds.size === 0) return
    setIsSending(true)
    try {
      await sendToKitchen()
      // Also fire the printed KITCHEN slip for the device's thermal. Best-effort:
      // if the merchant uses a KDS only setup (no paper), they can cancel the
      // browser print dialog and life goes on. v1.1 will skip this when station
      // routing config says "no paper for this station".
      try { await fireKitchenPrint.mutateAsync(order.id) } catch { /* noop */ }
      showToast('Enviado a cocina')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al enviar', 'error')
    } finally {
      setIsSending(false)
    }
  }

  async function handlePrecuenta() {
    if (!order) return
    try {
      const { bridgeOutcome } = await enqueuePrecuenta.mutateAsync(order)
      // Lane #160.B — toast diferenciado en función de lo que pasó con el
      // bridge NUC. Antes siempre se mostraba "enviada a impresora" aunque el
      // bridge hubiera fallado en silencio.
      if (bridgeOutcome === 'printed') {
        showToast('Precuenta enviada a impresora')
      } else if (bridgeOutcome === 'disabled') {
        // Opt-out explícito (localStorage dhk8_pos_bridge_disabled = '1') o
        // tenant sin bridge — el browser print sí cubre el caso.
        showToast('Precuenta lista (sin bridge)')
      } else {
        // 'failed' — bridge no respondió OK o order venía sin líneas.
        showToast('Precuenta NO llegó al NUC. Reintenta o revisa el bridge.', 'error')
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo imprimir precuenta', 'error')
    }
  }

  async function handleFlushOutbox() {
    try {
      const res = await flushOutbox()
      showToast(res.remaining === 0 ? `Sincronizado (${res.sent})` : `Sincronizados ${res.sent}, faltan ${res.remaining}`)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo sincronizar', 'error')
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────
  const itemCount = order?.items.reduce((s, i) => s + i.quantity, 0) ?? 0
  const hasPending = pendingItemIds.size > 0
  // subAccounts (when present) always starts with the synthetic "Principal" bucket;
  // an order "has subs" when there is at least one real sub-account beside it.
  const subAccounts = order?.subAccounts ?? []
  const hasSubs = subAccounts.some((s) => s.id !== PRINCIPAL_SUB_ID)
  const realSubs = useMemo(() => subAccounts.filter((s) => s.id !== PRINCIPAL_SUB_ID), [subAccounts])
  const visibleItems: OrderItem[] = useMemo(() => {
    if (!order) return []
    if (hasSubs && activeSub) {
      const sa = subAccounts.find((s) => s.id === activeSub)
      return sa ? sa.items : order.items
    }
    return order.items
  }, [order, hasSubs, activeSub, subAccounts])
  useEffect(() => {
    if (hasSubs && (activeSub === null || !subAccounts.some((s) => s.id === activeSub))) setActiveSub(subAccounts[0]?.id ?? null)
    if (!hasSubs && activeSub !== null) setActiveSub(null)
  }, [hasSubs, activeSub, subAccounts])

  // Cycle a line through: Principal → Cuenta A → Cuenta B → … → Principal.
  const subAccountIdOfItem = useMemo(() => {
    const m = new Map<string, string>()
    for (const sa of realSubs) for (const it of sa.items) m.set(it.id, sa.id)
    return m
  }, [realSubs])
  async function handleCycleItemAccount(itemId: string) {
    if (!hasSubs) return
    const order2 = [PRINCIPAL_SUB_ID, ...realSubs.map((s) => s.id)]
    const cur = subAccountIdOfItem.get(itemId) ?? PRINCIPAL_SUB_ID
    const idx = order2.indexOf(cur)
    const nextId = order2[(idx + 1) % order2.length] ?? PRINCIPAL_SUB_ID
    setBusyItemId(itemId)
    try {
      await assignItemToSub(itemId, nextId === PRINCIPAL_SUB_ID ? null : nextId)
      const label = nextId === PRINCIPAL_SUB_ID ? 'Principal' : (realSubs.find((s) => s.id === nextId)?.label ?? 'cuenta')
      showToast(`Movido a ${label}`)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo mover el ítem', 'error')
    } finally { setBusyItemId(null) }
  }
  const accountChipLabel = (itemId: string): string => {
    const id = subAccountIdOfItem.get(itemId)
    if (!id) return 'Principal'
    return realSubs.find((s) => s.id === id)?.label ?? 'Cuenta'
  }

  // Shared cart body — rendered inline as an aside on desktop (≥1024px) and inside
  // BottomSheet on mobile/tablet-portrait. Same JSX in both contexts so add/remove,
  // sub-account cycle, split, discount, send-to-kitchen, payment all keep working.
  const cartBody = order ? (
    <div className="dhk8-cmda__sheet-body">
      {hasSubs && (
        <div className="dhk8-cmda__subs" role="tablist" aria-label="Sub-cuentas">
          {subAccounts.map((sa) => (
            <button key={sa.id} type="button" role="tab" aria-selected={activeSub === sa.id} className={`dhk8-cmda__sub${activeSub === sa.id ? ' dhk8-cmda__sub--active' : ''}`} onClick={() => setActiveSub(sa.id)}>
              {sa.label} · {formatCents(sa.totalCents)}
            </button>
          ))}
        </div>
      )}
      <ul className="dhk8-cmda__items">
        {visibleItems.length === 0 ? (
          <li className="dhk8-cmda__items-empty">Agrega productos desde el menú.</li>
        ) : visibleItems.map((item) => {
          const isPending = pendingItemIds.has(item.id)
          return (
            <li key={item.id} className={`dhk8-cmda__item${isPending ? ' dhk8-cmda__item--pending' : ''}`}>
              <div className="dhk8-cmda__item-main">
                <span className="dhk8-cmda__item-qty">×{item.quantity}</span>
                <div className="dhk8-cmda__item-text">
                  <span className="dhk8-cmda__item-name">{item.name}</span>
                  {item.modifiers.length > 0 && <span className="dhk8-cmda__item-mods">{item.modifiers.map((m) => m.name).join(' · ')}</span>}
                  {item.notes !== undefined && <span className="dhk8-cmda__item-notes">“{item.notes}”</span>}
                </div>
                <span className="dhk8-cmda__item-total">{formatCents(item.totalCents)}</span>
              </div>
              <div className="dhk8-cmda__item-ops">
                <div className="dhk8-cmda__item-flags">
                  {isPending ? <span className="dhk8-cmda__item-flag">Por enviar</span> : <span className="dhk8-cmda__item-flag dhk8-cmda__item-flag--sent">{item.status}</span>}
                  {hasSubs && (
                    <button type="button" className="dhk8-cmda__item-acct" disabled={busyItemId === item.id}
                      aria-label={`Cuenta del ítem: ${accountChipLabel(item.id)} — tocar para cambiar`}
                      onClick={() => void handleCycleItemAccount(item.id)}>
                      {accountChipLabel(item.id)}
                    </button>
                  )}
                </div>
                <div className="dhk8-cmda__item-btns">
                  <button type="button" aria-label="Quitar uno" className="dhk8-cmda__iconbtn" disabled={busyItemId === item.id} onClick={() => void handleRemoveItem(item.id)}><Minus size={15} /></button>
                  <button type="button" aria-label="Agregar uno" className="dhk8-cmda__iconbtn" disabled={busyItemId === item.productId} onClick={() => void handleAddItemQty(item.productId)}><Plus size={15} /></button>
                  {/* Lane #172 — Cortesía (regalo 100%) per-item con gerente. */}
                  <button type="button" aria-label="Cortesía" title="Marcar como cortesía (gerente)" className="dhk8-cmda__iconbtn" disabled={busyItemId === item.id || item.totalCents <= 0} onClick={() => void handleCompItem(item.id)}><Gift size={15} /></button>
                  <button type="button" aria-label="Eliminar" className="dhk8-cmda__iconbtn" disabled={busyItemId === item.id} onClick={() => void handleRemoveItem(item.id)}><Trash2 size={15} /></button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  ) : null

  const cartFooter = order ? (
    <div className="dhk8-cmda__sheet-foot">
      <div className="dhk8-cmda__totals">
        <div className="dhk8-cmda__total-line"><span>Subtotal</span><span>{formatCents(order.subtotalCents)}</span></div>
        <div className="dhk8-cmda__total-line"><span>Impuestos</span><span>{formatCents(order.taxCents)}</span></div>
        {order.discountCents > 0 && <div className="dhk8-cmda__total-line"><span>Descuento</span><span>−{formatCents(order.discountCents)}</span></div>}
        <div className="dhk8-cmda__total-line dhk8-cmda__total-line--grand"><span>Total</span><span>{formatCents(order.totalCents)}</span></div>
      </div>
      <div className="dhk8-cmda__sheet-actions">
        <button type="button" className="dhk8-cmda__act" disabled={!hasPending || isSending} onClick={() => void handleSendToKitchen()}>
          <Flame size={15} /> {isSending ? 'Enviando…' : hasPending ? `Enviar a cocina (${pendingItemIds.size})` : 'Enviar a cocina'}
        </button>
        <button type="button" className="dhk8-cmda__act" disabled={order.items.length === 0} onClick={() => { setCartOpen(false); setSplitOpen(true) }}>
          <SplitSquareHorizontal size={15} /> Dividir
        </button>
        <button type="button" className="dhk8-cmda__act" disabled={order.status === 'CLOSED' || order.status === 'CANCELLED'} onClick={() => { setCartOpen(false); setDiscountOpen(true) }}>
          <Percent size={15} /> Descuento
        </button>
        <button type="button" className="dhk8-cmda__act" disabled={order.items.length === 0 || enqueuePrecuenta.isPending} onClick={() => void handlePrecuenta()}>
          <FileText size={15} /> {enqueuePrecuenta.isPending ? 'Imprimiendo…' : 'Precuenta'}
        </button>
        {/* Lane #172 — Anular comanda completa (gerente + razón). */}
        <button type="button" className="dhk8-cmda__act dhk8-cmda__act--danger" disabled={order.status === 'CLOSED' || order.status === 'CANCELLED'} onClick={() => void handleVoidOrder()}>
          <Ban size={15} /> Anular
        </button>
        <button type="button" className="dhk8-cmda__act dhk8-cmda__act--primary" disabled={order.items.length === 0 || order.status === 'CLOSED' || order.status === 'CANCELLED'} onClick={() => { setCartOpen(false); setPaymentOpen(true) }}>
          <DollarSign size={15} /> Cobrar {formatCents(order.totalCents)}
        </button>
      </div>
    </div>
  ) : null

  return (
    <div className="dhk8-cmda">
      {/* order-context strip — TableSelectorBar owns its layout (two-line header) */}
      <TableSelectorBar errorMessage={topError} />

      {syncing && <div className="dhk8-cmda__sync" role="status" aria-live="polite">Sincronizando…</div>}
      {outboxDepth > 0 && (
        <div className="dhk8-cmda__offline" role="status">
          <CloudOff size={14} />
          <span>{outboxDepth} cambio(s) pendiente(s)</span>
          <button type="button" onClick={() => void handleFlushOutbox()}>Reintentar</button>
        </div>
      )}

      {/* Desktop (≥1024px): 2-col grid menu + aside cart. Mobile: aside hides, menu
          takes full width, cart shows via BottomSheet on demand. */}
      <div className="dhk8-cmda__body">
      {/* full-content menu grid */}
      <div className="dhk8-cmda__menu">
        <div className="dhk8-cmda__search">
          <SearchIcon size={14} className="dhk8-cmda__search-icon" />
          <input className="dhk8-cmda__search-input" placeholder="Buscar producto o SKU…" value={search} onChange={(e) => setSearch(e.currentTarget.value)} aria-label="Buscar producto" />
          {search && <button type="button" className="dhk8-cmda__search-clear" aria-label="Limpiar" onClick={() => setSearch('')}><XIcon size={14} /></button>}
        </div>
        <div className="dhk8-cmda__cats" role="tablist" aria-label="Categorías">
          <button type="button" role="tab" aria-selected={activeCat === 'ALL'} className={`dhk8-cmda__cat${activeCat === 'ALL' ? ' dhk8-cmda__cat--active' : ''}`} onClick={() => setActiveCat('ALL')}>Todos</button>
          {categories.map((c) => (
            <button key={c.id} type="button" role="tab" aria-selected={activeCat === c.id} className={`dhk8-cmda__cat${activeCat === c.id ? ' dhk8-cmda__cat--active' : ''}`} onClick={() => setActiveCat(c.id)}>{c.name}</button>
          ))}
        </div>
        <div className="dhk8-cmda__grid">
          {menu.isLoading && <div className="dhk8-cmda__empty">Cargando menú…</div>}
          {menu.isError && <div className="dhk8-cmda__empty">No se pudo cargar el menú.</div>}
          {!menu.isLoading && !menu.isError && filtered.length === 0 && <div className="dhk8-cmda__empty">{search ? `Sin resultados para “${search}”.` : 'No hay productos disponibles.'}</div>}
          {!menu.isLoading && filtered.map((p) => {
            const disabled = !order || p.isAvailable === false || p.stockStatus === 'OUT'
            const extras = previewProductExtras(p.id)
            const customizable = !!extras.variants || (extras.modifierGroups?.length ?? 0) > 0
            return (
              <button key={p.id} type="button" className={`dhk8-cmda__card${disabled ? ' dhk8-cmda__card--off' : ''}`} disabled={disabled} onClick={() => handleSelectProduct(p)}
                aria-label={`${p.name} — ${formatCents(p.unitPriceCents)}${customizable ? ' — personalizable' : ''}`}>
                <span className="dhk8-cmda__card-thumb" aria-hidden="true">
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt="" loading="lazy" className="dhk8-cmda__card-img" />
                  ) : (
                    <span className="dhk8-cmda__card-initial">{p.name.trim().slice(0, 1).toUpperCase()}</span>
                  )}
                  {customizable && <span className="dhk8-cmda__card-tag dhk8-cmda__card-tag--overlay">Personalizar</span>}
                </span>
                <span className="dhk8-cmda__card-name">{p.name}</span>
                <div className="dhk8-cmda__card-meta">
                  <span className="dhk8-cmda__card-sku">{p.sku}</span>
                  <span className="dhk8-cmda__card-price">{formatCents(p.unitPriceCents)}</span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

        {/* Desktop-only inline cart aside (hidden on mobile via CSS). */}
        <aside className="dhk8-cmda__cart" aria-label={order ? `Comanda · ${order.folio}` : 'Comanda'}>
          <header className="dhk8-cmda__cart-head">
            <span className="dhk8-cmda__cart-title">{order ? `Comanda · ${order.folio}` : 'Comanda'}</span>
            <span className="dhk8-cmda__cart-count">{itemCount} ítem{itemCount === 1 ? '' : 's'}</span>
          </header>
          <div className="dhk8-cmda__cart-scroll">
            {order ? cartBody : <div className="dhk8-cmda__items-empty">Sin orden abierta — abre una mesa.</div>}
          </div>
          {cartFooter}
        </aside>
      </div>

      {/* sticky cart bar — mobile only (hidden on desktop via CSS) */}
      <button type="button" className="dhk8-cmda__bar" onClick={() => { if (order) setCartOpen(true) }} disabled={!order}>
        <ShoppingCart size={16} className="dhk8-cmda__bar-icon" />
        {order ? (
          <span className="dhk8-cmda__bar-text">Comanda · {itemCount} ítem{itemCount === 1 ? '' : 's'} · {formatCents(order.totalCents)}</span>
        ) : (
          <span className="dhk8-cmda__bar-text dhk8-cmda__bar-text--muted">Sin orden abierta — abre una mesa</span>
        )}
        {order && <span className="dhk8-cmda__bar-cta">Ver <ChevronRight size={14} /></span>}
      </button>

      {/* cart bottom sheet — mobile / tablet-portrait only. On desktop the cart
          renders inline in the <aside> above and the sheet stays closed. */}
      <BottomSheet
        open={cartOpen && !!order}
        onClose={() => setCartOpen(false)}
        title={order ? `Comanda · ${order.folio}` : 'Comanda'}
        heightFraction={0.82}
        footer={cartFooter ?? undefined}
      >
        {cartBody}
      </BottomSheet>

      <AddItemDialog product={pickedProduct} onClose={() => setPickedProduct(null)} onConfirm={handleAddItem} />

      <PaymentDialog
        open={paymentOpen}
        onClose={() => { setPaymentOpen(false); if (useOrderStore.getState().order?.status === 'CLOSED') resetOrder() }}
        onClosed={() => showToast('Orden cerrada — ticket enviado a impresora')}
      />

      <SplitDialog
        open={splitOpen}
        order={order}
        onClose={() => setSplitOpen(false)}
        onSplit={async (result: SplitResult) => {
          // Lane #166 — try/catch. Antes un fallo en splitOrder/rename propagaba
          // al ErrorBoundary y crasheaba toda la pantalla con 'algo salió mal'.
          // Ahora lo atrapamos y mostramos toast de error, sin crash.
          try {
            await splitOrder({
              mode: result.mode,
              ...(result.parts !== undefined ? { parts: result.parts } : {}),
              labels: result.labels,
              ...(result.percents ? { percents: result.percents } : {}),
              ...(result.itemAssignments ? { itemAssignments: result.itemAssignments } : {}),
            })
            const summary = result.mode === 'ITEMS'
              ? `Cuenta dividida en ${result.labels.length} sub-cuentas`
              : result.mode === 'PERCENT' ? 'Cuenta dividida por porcentaje'
              : `Dividido en ${result.parts ?? result.labels.length} partes iguales`
            showToast(summary)
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'No se pudo dividir la cuenta'
            showToast(`Error al dividir: ${msg}`, 'error')
          }
        }}
      />

      {discountOpen && <DiscountDialog onClose={() => setDiscountOpen(false)} />}

      {toast && <div className={`dhk8-cmda__toast dhk8-cmda__toast--${toast.kind}`}>{toast.msg}</div>}

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-cmda { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); height:100%; min-height:0; }
  .dhk8-cmda > :where(.dhk8-octx) { flex-shrink:0; }

  .dhk8-cmda__sync { flex-shrink:0; padding:var(--pos-space-1,4px) var(--pos-space-3,12px); background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-pill,999px); color:var(--pos-fg-tertiary,#7E8C82); font-size:var(--pos-text-xxs,11px); letter-spacing:.08em; text-transform:uppercase; animation:dhk8-cmda-pulse 1.2s ease-in-out infinite; align-self:flex-start; }
  @keyframes dhk8-cmda-pulse { 0%,100%{opacity:.5} 50%{opacity:1} }
  .dhk8-cmda__offline { flex-shrink:0; display:flex; align-items:center; gap:var(--pos-space-2,8px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); background:color-mix(in oklch, var(--pos-warning,#F1B33B) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-warning,#F1B33B) 40%, transparent); border-radius:var(--pos-radius-md,8px); color:var(--pos-warning,#F1B33B); font-size:var(--pos-text-sm,13px); }
  .dhk8-cmda__offline button { margin-left:auto; padding:var(--pos-space-1,4px) var(--pos-space-3,12px); min-height:36px; background:transparent; border:1px solid currentColor; border-radius:var(--pos-radius-sm,6px); color:inherit; font:inherit; cursor:pointer; }

  /* ── body wrapper (single column on mobile; 2-col grid on desktop) ── */
  .dhk8-cmda__body { flex:1; min-height:0; min-width:0; display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  /* Inline aside cart is hidden on mobile; the BottomSheet handles the cart there. */
  .dhk8-cmda__cart { display:none; }

  /* ── menu ── */
  .dhk8-cmda__menu { flex:1; min-height:0; min-width:0; display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-cmda__search { flex-shrink:0; position:relative; display:flex; align-items:center; gap:var(--pos-space-2,8px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:var(--pos-surface,#161C18); height:var(--pos-touch-target,44px); border-radius:var(--pos-radius-lg,12px); transition:border-color var(--pos-duration-fast,120ms) ease, box-shadow var(--pos-duration-fast,120ms) ease; }
  .dhk8-cmda__search:focus-within { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-cmda__search-icon { color:var(--pos-fg-tertiary,#7E8C82); flex-shrink:0; }
  .dhk8-cmda__search-input { flex:1; min-width:0; border:none; background:transparent; font-size:var(--pos-text-md,15px); color:var(--pos-fg,#E8EFE9); outline:none; }
  .dhk8-cmda__search-clear { flex-shrink:0; background:transparent; border:none; color:var(--pos-fg-tertiary,#7E8C82); cursor:pointer; padding:4px; display:flex; }
  .dhk8-cmda__search-clear:hover { color:var(--pos-fg,#E8EFE9); }
  .dhk8-cmda__cats { flex-shrink:0; display:flex; gap:var(--pos-space-2,8px); overflow-x:auto; padding-bottom:var(--pos-space-1,4px); }
  .dhk8-cmda__cat { padding:var(--pos-space-2,8px) var(--pos-space-4,16px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); letter-spacing:.01em; cursor:pointer; white-space:nowrap; border-radius:var(--pos-radius-pill,999px); min-height:var(--pos-touch-target,44px); transition:all var(--pos-duration-fast,120ms) ease; }
  .dhk8-cmda__cat:hover { background:var(--pos-elevated,#1E2520); }
  .dhk8-cmda__cat--active { background:var(--pos-tint,#1F8B3A); color:#fff; border-color:var(--pos-tint,#1F8B3A); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-cmda__grid { flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); grid-auto-rows:minmax(min-content,max-content); gap:var(--pos-space-3,12px); align-content:start; padding-bottom:var(--pos-space-4,16px); }
  .dhk8-cmda__empty { grid-column:1/-1; padding:var(--pos-space-6,24px); text-align:center; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-cmda__card { display:grid; grid-template-rows:auto 1fr auto; gap:8px; padding:var(--pos-space-2,8px); background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-md,8px); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); color:var(--pos-fg,#E8EFE9); text-align:left; cursor:pointer; transition:transform var(--pos-duration-fast,120ms) ease, background var(--pos-duration-fast,120ms) ease, border-color var(--pos-duration-fast,120ms) ease; overflow:hidden; }
  .dhk8-cmda__card:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); border-color:var(--pos-separator-strong,rgba(255,255,255,.14)); }
  .dhk8-cmda__card:active:not(:disabled) { transform:scale(.98); background:var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-cmda__card--off { opacity:.4; cursor:not-allowed; }
  .dhk8-cmda__card-thumb { position:relative; display:block; width:100%; aspect-ratio:1/1; background:var(--pos-bg,#0E1311); border-radius:var(--pos-radius-sm,6px); overflow:hidden; flex-shrink:0; min-height:0; }
  .dhk8-cmda__card-img { width:100%; height:100%; object-fit:cover; display:block; }
  .dhk8-cmda__card-initial { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-family:var(--font-sans,system-ui); font-size:clamp(28px,5vw,40px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg-tertiary,#7E8C82); background:linear-gradient(135deg,rgba(255,255,255,.04),rgba(255,255,255,0)); }
  .dhk8-cmda__card-tag--overlay { position:absolute; top:6px; right:6px; }
  .dhk8-cmda__card-name { font-family:var(--font-sans,system-ui); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); line-height:1.25; color:var(--pos-fg,#E8EFE9); display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:2.5em; }
  .dhk8-cmda__card-sku { font-family:var(--font-mono,ui-monospace); font-size:var(--pos-text-xxs,11px); letter-spacing:.02em; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-cmda__card-tag { font-family:var(--font-sans,system-ui); font-size:9px; font-weight:var(--pos-weight-semibold,600); letter-spacing:.06em; text-transform:uppercase; color:var(--pos-tint,#1F8B3A); padding:2px 7px; border-radius:var(--pos-radius-pill,999px); background:var(--pos-tint-soft,rgba(31,139,58,.12)); backdrop-filter:blur(6px); }
  .dhk8-cmda__card-price { font-family:var(--font-mono,ui-monospace); font-size:var(--pos-text-sm,13px); font-variant-numeric:tabular-nums; color:var(--pos-fg,#E8EFE9); }
  .dhk8-cmda__card-meta { display:flex; align-items:baseline; justify-content:space-between; gap:var(--pos-space-2,8px); }

  /* ── sticky cart bar ── */
  .dhk8-cmda__bar { flex-shrink:0; display:flex; align-items:center; gap:var(--pos-space-2,8px); padding:0 var(--pos-space-4,16px); min-height:var(--pos-touch-target,44px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-lg,12px); background:var(--pos-elevated,#1E2520); box-shadow:var(--pos-shadow-2,0 2px 6px -1px rgba(0,0,0,.25)); color:var(--pos-fg,#E8EFE9); font:inherit; cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-cmda__bar:hover:not(:disabled) { background:var(--pos-surface,#161C18); }
  .dhk8-cmda__bar:disabled { opacity:.6; cursor:not-allowed; }
  .dhk8-cmda__bar-icon { color:var(--pos-tint,#1F8B3A); flex-shrink:0; }
  .dhk8-cmda__bar-text { flex:1; min-width:0; font-size:var(--pos-text-sm,13px); font-variant-numeric:tabular-nums; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .dhk8-cmda__bar-text--muted { color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-cmda__bar-cta { flex-shrink:0; display:inline-flex; align-items:center; gap:2px; font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); color:var(--pos-tint,#1F8B3A); text-transform:uppercase; letter-spacing:.04em; }

  /* ── sheet body ── */
  .dhk8-cmda__sheet-body { display:flex; flex-direction:column; }
  .dhk8-cmda__subs { display:flex; gap:var(--pos-space-2,8px); overflow-x:auto; padding:0 var(--pos-space-4,16px) var(--pos-space-2,8px); }
  .dhk8-cmda__sub { padding:var(--pos-space-1,4px) var(--pos-space-3,12px); min-height:36px; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font-size:var(--pos-text-xs,12px); white-space:nowrap; border-radius:var(--pos-radius-pill,999px); cursor:pointer; }
  .dhk8-cmda__sub--active { background:var(--pos-tint,#1F8B3A); color:#fff; border-color:var(--pos-tint,#1F8B3A); }
  .dhk8-cmda__items { list-style:none; margin:0; padding:0; }
  .dhk8-cmda__items-empty { padding:var(--pos-space-6,24px); text-align:center; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-cmda__item { padding:var(--pos-space-3,12px) var(--pos-space-4,16px); border-bottom:1px solid var(--pos-separator,rgba(255,255,255,.07)); display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-cmda__item--pending { background:color-mix(in oklch, var(--pos-warning,#F1B33B) 8%, transparent); }
  .dhk8-cmda__item-main { display:grid; grid-template-columns:auto 1fr auto; gap:var(--pos-space-3,12px); align-items:baseline; }
  .dhk8-cmda__item-qty { font-family:var(--font-mono,ui-monospace); font-size:var(--pos-text-sm,13px); color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-cmda__item-text { display:flex; flex-direction:column; gap:3px; min-width:0; }
  .dhk8-cmda__item-name { font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); color:var(--pos-fg,#E8EFE9); line-height:1.25; }
  .dhk8-cmda__item-mods, .dhk8-cmda__item-notes { font-size:var(--pos-text-xxs,11px); line-height:1.3; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-cmda__item-notes { font-style:italic; }
  .dhk8-cmda__item-total { font-family:var(--font-mono,ui-monospace); font-size:var(--pos-text-xs,12px); font-variant-numeric:tabular-nums; color:var(--pos-fg,#E8EFE9); }
  .dhk8-cmda__item-ops { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-2,8px); }
  .dhk8-cmda__item-flags { display:flex; align-items:center; gap:var(--pos-space-2,8px); min-width:0; flex-wrap:wrap; }
  .dhk8-cmda__item-flag { font-size:9px; letter-spacing:.06em; text-transform:uppercase; color:var(--pos-warning,#F1B33B); }
  .dhk8-cmda__item-flag--sent { color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-cmda__item-acct { min-height:36px; padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-pill,999px); background:var(--pos-tint-soft,rgba(31,139,58,.12)); color:var(--pos-tint,#1F8B3A); font:inherit; font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); cursor:pointer; white-space:nowrap; max-width:120px; overflow:hidden; text-overflow:ellipsis; transition:filter var(--pos-duration-fast,120ms) ease; }
  .dhk8-cmda__item-acct:hover:not(:disabled) { filter:brightness(1.1); }
  .dhk8-cmda__item-acct:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-cmda__item-btns { display:flex; gap:var(--pos-space-1,4px); }
  .dhk8-cmda__iconbtn { display:flex; align-items:center; justify-content:center; width:44px; height:44px; border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-sm,6px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-cmda__iconbtn:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); }
  .dhk8-cmda__iconbtn:disabled { opacity:.4; cursor:not-allowed; }

  /* ── sheet footer ── */
  .dhk8-cmda__sheet-foot { display:flex; flex-direction:column; }
  .dhk8-cmda__totals { padding:var(--pos-space-2,8px) var(--pos-space-4,16px); display:flex; flex-direction:column; gap:2px; }
  .dhk8-cmda__total-line { display:flex; justify-content:space-between; font-size:var(--pos-text-xs,12px); font-variant-numeric:tabular-nums; color:var(--pos-fg-secondary,#B6BFB8); }
  .dhk8-cmda__total-line--grand { font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); padding-top:var(--pos-space-1,4px); border-top:1px solid var(--pos-separator,rgba(255,255,255,.07)); margin-top:2px; }
  .dhk8-cmda__sheet-actions { display:grid; grid-template-columns:1fr 1fr; gap:var(--pos-space-2,8px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-cmda__act { display:inline-flex; align-items:center; justify-content:center; gap:var(--pos-space-2,8px); min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-surface,#161C18); color:var(--pos-fg,#E8EFE9); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-cmda__act:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-cmda__act:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-cmda__act--primary { grid-column:1/-1; background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); color:#fff; box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-cmda__act--primary:hover:not(:disabled) { background:var(--pos-tint,#1F8B3A); filter:brightness(1.06); }
  /* Lane #172 — variante danger para Anular comanda. Borde + texto rojos sobre
     surface oscuro; mismas dimensiones que el resto para no romper la grilla. */
  .dhk8-cmda__act--danger { border-color:var(--pos-danger,#E5484D); color:var(--pos-danger,#E5484D); }
  .dhk8-cmda__act--danger:hover:not(:disabled) { background:color-mix(in oklch, var(--pos-danger,#E5484D) 12%, transparent); }

  .dhk8-cmda__toast { position:fixed; bottom:var(--pos-space-6,24px); left:50%; transform:translateX(-50%); padding:var(--pos-space-3,12px) var(--pos-space-5,20px); font-size:var(--pos-text-sm,13px); z-index:var(--pos-z-toast, 300); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-pill,999px); background:var(--pos-elevated,#1E2520); box-shadow:var(--pos-shadow-3,0 8px 20px -6px rgba(0,0,0,.35)); }
  .dhk8-cmda__toast--success { border-color:color-mix(in oklch, var(--pos-tint,#1F8B3A) 50%, transparent); color:var(--pos-success,#8BC34A); }
  .dhk8-cmda__toast--error { background:color-mix(in oklch, var(--pos-danger,#E5484D) 16%, var(--pos-elevated,#1E2520)); border-color:color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); color:var(--pos-danger,#E5484D); }
  /* Phone (≤520px): 2 columnas para aprovechar el espacio (1 por fila ocupaba demasiado). */
  @media (max-width: 520px) { .dhk8-cmda__grid { grid-template-columns:repeat(2, 1fr); gap:var(--pos-space-2,8px); } }

  /* Desktop (≥1024px): split the body into a scrolling menu column on the left and
     a fixed-width cart aside on the right (FastBarPage parity). Hide the sticky
     "Comanda · …" bar and the BottomSheet trigger entirely — the cart is always
     visible inline. The BottomSheet is still rendered (cartOpen stays false on
     desktop in practice), but if it ever opens it would just stack over both
     columns — fine for emergency fall-through. */
  @media (min-width: 1024px) {
    .dhk8-cmda__body { display:grid; grid-template-columns:1fr minmax(320px, 380px); gap:var(--pos-space-4,16px); align-items:stretch; }
    .dhk8-cmda__menu { min-height:0; }
    .dhk8-cmda__cart { display:flex; flex-direction:column; min-height:0; background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-lg,12px); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); overflow:hidden; }
    .dhk8-cmda__cart-head { flex-shrink:0; display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-2,8px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px); border-bottom:1px solid var(--pos-separator,rgba(255,255,255,.07)); }
    .dhk8-cmda__cart-title { font-family:var(--font-display,system-ui); font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
    .dhk8-cmda__cart-count { font-family:var(--font-mono,ui-monospace); font-size:var(--pos-text-xxs,11px); letter-spacing:.06em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
    .dhk8-cmda__cart-scroll { flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; }
    .dhk8-cmda__cart .dhk8-cmda__sheet-foot { flex-shrink:0; border-top:1px solid var(--pos-separator,rgba(255,255,255,.07)); background:var(--pos-elevated,#1E2520); }
    /* Hide the mobile-only sticky cart trigger button. */
    .dhk8-cmda > .dhk8-cmda__bar { display:none; }
  }
`
